import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
} from '@mui/material';
import { QuestionString, DigitalSubmissionString, QuestionTypeV2 } from '../../../redux/types';
import useDebounce from '../../../hooks/useDebounce';
import useQuestionSaveStatus, { QuestionSaveOp } from '../../../hooks/survey/useQuestionSaveStatus';

export type Props = {
  question: QuestionString;
  sectionId?: number;
  enrollmentId?: number;
  caseId?: number;
  disabled?: boolean;
  defaultSubmission?: DigitalSubmissionString;
  required?: boolean;
  onSave?: (answer: DigitalSubmissionString | Omit<DigitalSubmissionString, 'id'> | undefined) => Promise<void>;
  getSaveStatus?: (op: QuestionSaveOp, qId: number, caseId?: number) => void;
  manualSave?: boolean;
  saveOnBlur?: boolean;
}

const StringQuestionInput: React.FC<Props> = ({
  question,
  sectionId,
  enrollmentId,
  caseId,
  disabled,
  defaultSubmission,
  required,
  onSave,
  getSaveStatus,
  manualSave,
  saveOnBlur,
}: Props) => {
  const [saveStatus, setSaveStatus] = useQuestionSaveStatus(question.id, caseId);
  const [answer, setAnswer] = useState<DigitalSubmissionString['answer']['answer'] | ''>(defaultSubmission?.answer.answer || '');
  const [error, setError] = useState<string>();

  useEffect(() => {
    setAnswer(defaultSubmission?.answer.answer || '');
  }, [defaultSubmission, caseId]);

  useEffect(() => {
    setSaveStatus('idle');
    setError(undefined);
  }, [caseId, sectionId, enrollmentId, setSaveStatus]);

  useEffect(() => {
    if (getSaveStatus) {
      getSaveStatus(saveStatus.op, saveStatus.questionId, saveStatus.caseId);
    }
  }, [getSaveStatus, saveStatus]);

  const getAnswerObj = (newAns: string): DigitalSubmissionString | Omit<DigitalSubmissionString, 'id'> | undefined => {
    if (!enrollmentId || !sectionId) {
      return undefined;
    }
    if (defaultSubmission) {
      return {
        ...defaultSubmission,
        answer: { answer: newAns },
      } as DigitalSubmissionString;
    }
    const newAnswer: Omit<DigitalSubmissionString, 'id'> = {
      answer: { answer: newAns },
      digitalQuestionId: question.id,
      digitalSectionId: sectionId,
      enrollmentId,
      type: QuestionTypeV2.String,
      digitalCaseId: caseId,
    };
    return newAnswer;
  };

  const saveSubmission = (input: string) => {
    if (onSave) {
      setSaveStatus('saving');
      onSave(getAnswerObj(input))
        .then(() => {
          setSaveStatus('saved');
        })
        .catch((e) => {
          setError((e as Error).message);
          setSaveStatus('idle');
        });
    }
  };

  const debounceSaveHandler = (debouncedAnswer: string) => {
    if (manualSave) {
      return;
    }
    if (saveStatus.op === 'saving') {
      return;
    }
    saveSubmission(debouncedAnswer);
  };

  const saveOnClickHandler = () => {
    if (!manualSave) {
      return;
    }
    if (saveStatus.op === 'saving') {
      return;
    }
    saveSubmission(answer);
  };

  const saveOnBlurHandler = () => {
    if (!saveOnBlur) {
      return;
    }
    if (saveStatus.op === 'saving') {
      return;
    }
    saveSubmission(answer);
  };

  const saveDebounce = useDebounce<string>(debounceSaveHandler, 1000);

  const onTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (saveStatus.op === 'saving') {
      return;
    }
    setAnswer(e.target.value);
    setError(undefined);
    if (!manualSave && !saveOnBlur) {
      setSaveStatus('debounceStarting');
      saveDebounce(e.target.value);
    }
  };

  return (
    <Box>
      <Typography fontWeight={500} gutterBottom>
        {question.text}
      </Typography>
      <Box>
        <TextField
          size="small"
          id="string-question-input"
          value={answer}
          onChange={onTextChange}
          fullWidth
          disabled={disabled}
          required={required}
          error={!!error}
          autoComplete="off"
          placeholder={question.placeholder || ''}
          onBlur={saveOnBlurHandler}
          onFocus={() => setSaveStatus('focused')}
        />
      </Box>
      {
        saveStatus.op === 'saved' && saveStatus.caseId === caseId && (
          <Box display="flex">
            <Typography color="primary.main" justifyContent="flex-end" variant="caption">
              <FormattedMessage id="submission.saved" />
            </Typography>
          </Box>
        )
      }
      {
        saveStatus.op === 'saving' && saveStatus.caseId === caseId && (
          <Box display="flex">
            <Typography justifyContent="flex-end" variant="caption">
              <FormattedMessage id="submission.saving" />
            </Typography>
          </Box>
        )
      }
      {
        error && (
          <Typography color="secondary" variant="caption">{error}</Typography>
        )
      }
      {
        manualSave && (
          <Box display="flex" justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button
              variant="contained"
              disabled={saveStatus.op === 'saving'}
              onClick={saveOnClickHandler}
              startIcon={saveStatus.op === 'saving' && (
                <CircularProgress sx={{ color: 'primary.dark' }} size={18} />
              )}
            >
              <FormattedMessage id="save" />
            </Button>
          </Box>
        )
      }
    </Box>
  );
};

export default StringQuestionInput;
